import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  Newspaper,
  Pencil,
  PlayCircle,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

import {
  getMateriaisControllerListarQueryKey,
  useMateriaisControllerExcluir,
  useMateriaisControllerListar,
} from '@/api/generated/materiais/materiais';
import type { MeuMaterialResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { NotaEstrelas } from '@/features/dashboard/components/NotaEstrelas';
import { MaterialFormDialogo } from '@/features/portal-parceiro/components/MaterialFormDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';

const STATUS = {
  PENDENTE: {
    rotulo: 'Aguardando aprovação',
    icone: Clock,
    classe: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-200',
  },
  APROVADO: {
    rotulo: 'Publicado',
    icone: CheckCircle2,
    classe:
      'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200',
  },
  REJEITADO: {
    rotulo: 'Rejeitado',
    icone: XCircle,
    classe: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-200',
  },
} as const;

/**
 * Portal do parceiro — "Meus materiais" (B8-7).
 *
 * ⚠️ O motivo da rejeição fica em destaque, não escondido num tooltip: é o
 * texto com o qual o parceiro corrige e reenvia. Na V1 o rejeitado era beco sem
 * saída (só dava para excluir e recriar); aqui editar já devolve à fila.
 */
export function MeusMateriaisPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<MeuMaterialResponse | undefined>();
  const [paraExcluir, setParaExcluir] = useState<MeuMaterialResponse | undefined>();

  const { data, isPending, isError, refetch } = useMateriaisControllerListar();
  const excluir = useMateriaisControllerExcluir();

  const podeCriar = pode('materiais.criar');
  const podeEditar = pode('materiais.editar');
  const podeExcluir = pode('materiais.excluir');

  function abrirNovo() {
    setEmEdicao(undefined);
    setFormAberto(true);
  }

  function confirmarExclusao() {
    if (!paraExcluir) return;

    excluir.mutate(
      { id: paraExcluir.id },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getMateriaisControllerListarQueryKey() });
          toast.success('Material removido');
          setParaExcluir(undefined);
        },
        onError: (erro) =>
          toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível remover'),
      },
    );
  }

  if (isError) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Newspaper className="w-7 h-7 text-brand" />
            Meus materiais
          </h2>
          <p className="text-muted-foreground mt-1">
            Envie vídeos e PDFs para a vitrine. Cada material passa pela aprovação da equipe.
          </p>
        </div>

        {podeCriar && (
          <Button onClick={abrirNovo} className="gap-2 bg-brand text-brand-foreground hover:bg-brand/90">
            <Plus className="w-4 h-4" />
            Novo material
          </Button>
        )}
      </div>

      {isPending ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      ) : data.itens.length === 0 ? (
        <EstadoVazio
          icone={Newspaper}
          titulo="Nenhum material enviado"
          descricao="Comece enviando um vídeo do YouTube ou um PDF."
          acao={
            podeCriar ? (
              <Button onClick={abrirNovo} className="gap-2 bg-brand text-brand-foreground">
                <Plus className="w-4 h-4" />
                Novo material
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {data.itens.map((material) => {
            const status = STATUS[material.status as keyof typeof STATUS];
            const IconeDeStatus = status.icone;
            const IconeDeTipo = material.tipo === 'VIDEO' ? PlayCircle : FileText;

            return (
              <Card key={material.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                        <IconeDeTipo className="w-5 h-5 text-brand" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate">{material.titulo}</h3>
                        {material.descricao && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {material.descricao}
                          </p>
                        )}

                        {material.url && (
                          <a
                            href={material.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-brand mt-2 hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            {material.tipo === 'VIDEO' ? 'Ver vídeo' : 'Abrir PDF'}
                          </a>
                        )}

                        {material.status === 'APROVADO' && (
                          <div className="mt-2">
                            <NotaEstrelas
                              nota={material.notaMedia}
                              total={material.totalAvaliacoes}
                            />
                          </div>
                        )}

                        {material.status === 'REJEITADO' && material.motivoRejeicao && (
                          <p className="text-xs text-destructive mt-2 p-2 rounded border border-destructive/30 bg-destructive/5">
                            <strong>Motivo da rejeição:</strong> {material.motivoRejeicao}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Badge className={`${status.classe} border gap-1 font-medium`}>
                        <IconeDeStatus className="w-3 h-3" />
                        {status.rotulo}
                      </Badge>

                      {material.editavel && (
                        <div className="flex gap-1">
                          {podeEditar && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Editar"
                              onClick={() => {
                                setEmEdicao(material);
                                setFormAberto(true);
                              }}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {podeExcluir && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              title="Excluir"
                              onClick={() => setParaExcluir(material)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {formAberto && (
        <MaterialFormDialogo
          aberto={formAberto}
          aoFechar={() => {
            setFormAberto(false);
            setEmEdicao(undefined);
          }}
          {...(emEdicao ? { material: emEdicao } : {})}
        />
      )}

      <AlertDialog open={!!paraExcluir} onOpenChange={(v) => !v && setParaExcluir(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir material</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{paraExcluir?.titulo}&quot; será removido. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmarExclusao}
              disabled={excluir.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
